import { axiosInstance as axios } from 'Api';
import { Config } from 'Constants/config_env';
import { handlingResponse, randomHash } from '../../utils/globalMethods';

/**  @function
 * * retrieve the jwt access token
 *
 * @name getJWT
 * @returns {object} the object contains the jwt
 */
export const getJWT = async (params) => {
	let response;
	try {
		response = await axios({
      headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			method: 'post',
			url: Config.token_service,
			data: params,
		});
		return handlingResponse(response);
	} catch (error) {
		console.error(error);
	}
};

/**  @function
 * * retrieve claims from token
 *
 * @name getClaims
 * @returns {getClaims} claims of the jwt
 */
export const getClaims = async (jwt) => {
	let response;
	try {
		response = await axios({
      headers: {
				'Content-Type': 'application/json',
				'Authorization': jwt,
			},
			method: 'get',
			url: 'https://intwallet2.nexiprivate.it/wallet20/api/config/getClaims',
		});
		return handlingResponse(response);
	} catch (error) {
		console.error(error);
	}
};

/**  @function
 * * made external user login
 *
 * @name externalFederation
 * @returns
 */
export const externalFederation = async (data) => {
	let response;
	try {
		response = await axios({
			method: 'post',
      data,
			url: Config.federate_external_login,
		});
		return handlingResponse(response);
	} catch (error) {
		console.error(error);
	}
};

/**  @function
 * * login for internal user
 *
 * @name internalfederation
 * @returns {object} the object contains the jwt
 */
export const internalFederation = async () => {
  window.sessionStorage.setItem('isXCORP', true);
  location.href = Config.federate_internal_login;
}

/**  @function
 * * login for internal user
 *
 * @name retrieveCodeHash
 * @returns undefined
 */
export const retrieveCodeHash = () => {
	if ((location.search.length > 0 || location.hash.length > 0) && !/user-enrollment/.test(location.href)) {
    const search = location.search.length > 0 ? location.search.substring(1) : location.hash.substring(1);
    const object = JSON.parse(`{"${search.replace(/&/g, '","').replace(/=/g, '":"')}"}`, (key, value) => (key === '' ? value : decodeURIComponent(value)));
    if(Reflect.has(object, 'error_code')) {
      window.sessionStorage.setItem('loginError', true);
    } else {
      window.sessionStorage.setItem('codeHash', JSON.stringify(object));
      if(window.sessionStorage.getItem('isXCORP') !== null) {
				const { codeVerifier } = JSON.parse(window.sessionStorage.getItem('oauth2_params'))

        const urlencoded = new URLSearchParams();
        urlencoded.append('grant_type', 'authorization_code');
        urlencoded.append('code', object.code);
        urlencoded.append('client_id', Config.client_id);
        urlencoded.append('code_verifier', codeVerifier);
        urlencoded.append('redirect_uri', Config.redirect_uri);

        getJWT(urlencoded).then((r) => {
          if (r.response.data) {
            window.sessionStorage.setItem('jwt', r.response.data.access_token);
            window.sessionStorage.removeItem('isAuthorized')
            window.sessionStorage.removeItem('codeHash')
            window.sessionStorage.removeItem('oauth2_params')
            window.location.replace('/');
          } else {
            return false;
          }
        });
      } else {
        window.location.replace(Config.redirect_uri);
      }
    }
	}
};

/**  @function
 * * Starting the ACGFlow
 *
 * @name getAuth
 * @returns {boolean}
 */

export const getAuth = (params) => {
	const { clientId, redirectUri, scope, authUrl } = { ...params };
	const state = randomHash('alnum', 16);
	const nonce = randomHash('alnum', 16);

	generatePKCECodeChallengeAndVerifier().then(({ codeChallenge, codeVerifier }) => {
		const urlencoded = new URLSearchParams();
		urlencoded.append('response_type', 'code');
		urlencoded.append('redirect_uri', redirectUri);
		urlencoded.append('client_id', clientId);
		urlencoded.append('scope', scope);
		urlencoded.append('code_challenge_method', 'S256');
		urlencoded.append('state', state);
		urlencoded.append('nonce', nonce);
		urlencoded.append('code_challenge', codeChallenge);

		const url = `${authUrl}?${urlencoded}`;
		window.sessionStorage.setItem('isAuthorized', true);
		window.sessionStorage.setItem('oauth2_params', JSON.stringify({
			codeChallenge,
			codeVerifier,
			state,
			nonce
		}));

		setTimeout(() => {
			window.location.replace(url);
		}, 2500);
	});

};

/**
 * Implements *base64url-encode* (RFC 4648 § 5) without padding, which is NOT
 * the same as regular base64 encoding.
 */
const base64urlEncode = (value) => {
	let base64 = btoa(value);
	base64 = base64.replace(/\+/g, '-');
	base64 = base64.replace(/\//g, '_');
	base64 = base64.replace(/=/g, '');
	return base64;
};

/**
 * Character set to generate code verifier defined in rfc7636.
 */
const PKCE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/**
 * Generate a code_verifier and code_challenge, as specified in rfc7636.
 */
export const generatePKCECodeChallengeAndVerifier = async () => {
	const output = new Uint32Array(96);
	window.crypto.getRandomValues(output);

	const codeVerifier = base64urlEncode(
		Array
			.from(output)
			.map(num => PKCE_CHARSET[num % PKCE_CHARSET.length])
			.join('')
	);

	const buffer = await window.crypto
			.subtle
			.digest('SHA-256', (new TextEncoder()).encode(codeVerifier));
	const hash = new Uint8Array(buffer);

	let binary = '';
	const hashLength = hash.byteLength;
	for (let i = 0; i < hashLength; i++) {
			binary += String.fromCharCode(hash[i]);
	}

	const codeChallenge = base64urlEncode(binary);
	return { codeChallenge, codeVerifier };
};
